import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback } from 'react';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { SearchParam } from '@common/ts';
import { ChevronDown, Check, Loader2, Search } from 'lucide-react';
import useTranslation from '@/hooks/useTranslation';
import QuestionItem from '@/components/QuestionItem/QuestionItem';
import QuestionSidebar from '@/components/QuestionItem/QuestionSidebar';
import QuestionStatsBar from '@/components/QuestionItem/QuestionStatsBar';
import useReportStore from '@/store/ReportStore';
import FullPageLoader from '@/components/atoms/Loader/FullPageLoader';
import NavigationControls from '@/components/NavigationControls/NavigationControls';
import { cn } from '@/lib/utils';
import { useGetInterviewQuestions, useGetProjectQuestionInsights, useGetQuestionCategories } from '@/reactQuery/get';
import useCategoryStore from '@/store/CategoryStore';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { ConversationState } from '@/enums';
import { InterviewQuestion } from '@/types';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface QuestionsProps {
  isPendingReRun: boolean;
  analysisLanguage: string;
  newConversationsCount: number;
  onReRun: () => void;
  isRerunLoading: boolean;
  newConversationsState: ConversationState;
  isLoadingBeforeApplyFilter: boolean;
  setSelectedQuestionId: (questionId: string) => void;
  selectedQuestionId: string;
}

function Questions({
  isPendingReRun,
  analysisLanguage,
  newConversationsCount,
  onReRun,
  isRerunLoading,
  newConversationsState,
  isLoadingBeforeApplyFilter,
  setSelectedQuestionId,
  selectedQuestionId,
}: QuestionsProps) {
  const { lang } = useTranslation();
  const { project_id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const filterId = searchParams.get(SearchParam.FILTER_ID);
  const { currentProject: singleProjectData } = useCurrentProject();
  const { currentTheme } = useTheme();

  const [selectedQuestionText, setSelectedQuestionText] = useState<string>('');
  const setIsQuestionDataSelected = useReportStore((state) => state.setIsQuestionDataSelected);

  const { data: interviewQuestions, isPending: isPendingInterviewQuestions } =
    useGetInterviewQuestions(filterId, projectId);

  const {
    data: selectedQuestionInsights,
    isPending: isPendingInsights,
    error: errorInsights,
  } = useGetProjectQuestionInsights(filterId, projectId, selectedQuestionId);

  const categoryType = useCategoryStore((state) => state.categoryType);
  const { data: categoryData } = useGetQuestionCategories(filterId, projectId, selectedQuestionId);

  const selectedQuestion = interviewQuestions?.find((item) => item.id === selectedQuestionId);
  const selectedQuestionMedia = selectedQuestion?.media || [];

  // Pick the active category set based on store mode
  const activeCategoryList = categoryType === 'multiple'
    ? categoryData?.categories_multi
    : categoryData?.categories;

  const onQuestionChange = (questionId: string) => {
    const question = interviewQuestions?.find((item) => item.id === questionId);
    if (!question) return;

    setSelectedQuestionId(questionId);
    setSelectedQuestionText(question?.question);
    setIsQuestionDataSelected(false);
  };

  useEffect(() => {
    setSelectedQuestionId(interviewQuestions?.[0]?.id ?? '');
    setSelectedQuestionText(interviewQuestions?.[0]?.question ?? '');
  }, [interviewQuestions]);

  const currentIndex = interviewQuestions?.findIndex((q) => q.id === selectedQuestionId) ?? 0;
  const totalQuestions = interviewQuestions?.length ?? 0;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < totalQuestions - 1;

  const goToPrev = () => {
    if (hasPrev && interviewQuestions) onQuestionChange(interviewQuestions[currentIndex - 1].id);
  };
  const goToNext = () => {
    if (hasNext && interviewQuestions) onQuestionChange(interviewQuestions[currentIndex + 1].id);
  };

  // Combobox state
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setHighlightedIndex(0);
    }, 150);
  }, []);

  const filteredQuestions =
    interviewQuestions?.filter((item: InterviewQuestion) =>
      item.question.toLowerCase().includes(debouncedSearch.toLowerCase()),
    ) ?? [];

  const handleOpenChange = (open: boolean) => {
    setPopoverOpen(open);
    if (!open) {
      setSearchQuery('');
      setDebouncedSearch('');
      setHighlightedIndex(0);
    }
  };

  // Auto-focus search input when popover opens
  useEffect(() => {
    if (popoverOpen) {
      // Small delay to let the popover render
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [popoverOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (!listRef.current) return;
    const highlighted = listRef.current.querySelector<HTMLElement>('[data-highlighted="true"]');
    highlighted?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, filteredQuestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = filteredQuestions[highlightedIndex];
      if (item) {
        onQuestionChange(item.id);
        handleOpenChange(false);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleOpenChange(false);
    }
  };

  if (errorInsights) return null;
  if (isPendingInsights || isLoadingBeforeApplyFilter) return <FullPageLoader />;

  return (
    <div className="w-full">
      {/* Question header with navigation — full width above the grid */}
      <div className="mb-5">
        {/* Question text + stepper in one row */}
        <div className="flex items-start gap-4">
          {/* Question title — clickable to open combobox */}
          <div className="relative flex-1 min-w-0">
            <Popover open={popoverOpen} onOpenChange={handleOpenChange}>
              <PopoverTrigger asChild>
                <button type="button" className="flex items-start gap-2 w-full text-left group">
                  <h2 className="text-xl font-semibold text-neutral-900 leading-snug group-hover:text-neutral-700 transition-colors line-clamp-2">
                    {isPendingInterviewQuestions ? (
                      <Loader2
                        style={{ color: currentTheme?.primary }}
                        className="h-5 w-5 animate-spin"
                      />
                    ) : (
                      selectedQuestionText
                    )}
                  </h2>
                  <ChevronDown
                    className={cn(
                      'h-5 w-5 text-neutral-400 shrink-0 mt-1 transition-transform',
                      popoverOpen && 'rotate-180',
                    )}
                  />
                </button>
              </PopoverTrigger>

              <PopoverContent
                align="start"
                sideOffset={8}
                className="p-0 rounded-xl border border-neutral-200 shadow-lg w-[var(--radix-popover-trigger-width)] max-w-none"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                {/* Search input */}
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-neutral-100">
                  <Search className="h-4 w-4 text-neutral-400 shrink-0" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Search questions…"
                    className="flex-1 text-sm text-neutral-700 placeholder:text-neutral-400 bg-transparent outline-none"
                  />
                </div>

                {/* Question list */}
                <div ref={listRef} className="max-h-[360px] overflow-y-auto">
                  {filteredQuestions.length === 0 ? (
                    <p className="px-4 py-6 text-sm text-neutral-400 text-center">
                      No matching questions
                    </p>
                  ) : (
                    filteredQuestions.map((item: InterviewQuestion, index: number) => {
                      // Resolve the original index for the number badge
                      const originalIndex =
                        interviewQuestions?.findIndex((q) => q.id === item.id) ?? index;
                      const isSelected = item.id === selectedQuestionId;
                      const isHighlighted = index === highlightedIndex;

                      return (
                        <button
                          key={item.id}
                          type="button"
                          data-highlighted={isHighlighted}
                          onClick={() => {
                            onQuestionChange(item.id);
                            handleOpenChange(false);
                          }}
                          onMouseEnter={() => setHighlightedIndex(index)}
                          className={cn(
                            'flex items-start gap-3 w-full text-left px-4 py-3 transition-colors border-b border-neutral-100 last:border-b-0',
                            isSelected && 'bg-emerald-50/50',
                            isHighlighted && !isSelected && 'bg-neutral-50',
                          )}
                        >
                          <span className="text-sm font-semibold text-neutral-400 shrink-0 mt-0.5 w-6">
                            {originalIndex + 1}
                          </span>
                          <span className="text-sm text-neutral-700 leading-snug flex-1">
                            {item.question}
                          </span>
                          {isSelected && (
                            <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Stepper — right side */}
          <div className="shrink-0 pt-0.5">
            <NavigationControls
              onPrev={goToPrev}
              onNext={goToNext}
              isPrevDisabled={!hasPrev}
              isNextDisabled={!hasNext}
              label={`${lang.get('msg.question')} ${currentIndex + 1} / ${totalQuestions}`}
            />
          </div>
        </div>
      </div>

      {/* 2-column grid: main content + sticky sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5 items-start">
        {/* Main column */}
        <div className="min-w-0">
          {selectedQuestionId && (
            <QuestionStatsBar
              questionId={selectedQuestionId}
              responseCount={categoryData?.conversations_num}
              patternCount={selectedQuestionInsights?.patterns_and_trends?.length}
              divergentCount={selectedQuestionInsights?.divergent_views_and_outliers?.length}
              quoteCount={selectedQuestionInsights?.voice_quotes?.length}
            />
          )}
          <QuestionItem
            isPendingReRun={isPendingReRun}
            selectedQuestionInsights={selectedQuestionInsights}
            questionID={selectedQuestionId}
            questionText={selectedQuestionText}
            analysisLanguage={analysisLanguage}
            singleProjectData={singleProjectData}
            newConversationsCount={newConversationsCount}
            onReRun={onReRun}
            isRerunLoading={isRerunLoading}
            newConversationsState={newConversationsState}
            projectId={projectId}
            isPendingInterviewQuestions={isPendingInterviewQuestions}
            questionStimuli={selectedQuestionMedia}
          />
        </div>

        {/* Sidebar — hidden on small screens, sticky on large */}
        {selectedQuestionId && (
          <div className="hidden lg:flex flex-col gap-4 sticky top-4">
            <QuestionSidebar
              questionId={selectedQuestionId}
              responseCount={categoryData?.conversations_num}
              categories={activeCategoryList}
              patternCount={selectedQuestionInsights?.patterns_and_trends?.length}
              divergentCount={selectedQuestionInsights?.divergent_views_and_outliers?.length}
              quoteCount={selectedQuestionInsights?.voice_quotes?.length}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Questions;
